import { Globe, Edit, X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { currencies, getDefaultCurrency } from "@/components/utils/currencies";
import EditableInput from "@/components/ui/editable-input";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from '@/hooks/useI18n';
import { toast } from "@/hooks/useToast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

// Language and currency section component with separate edit state
interface LanguageCurrencySectionProps {
  isEditing?: boolean;
  onEditToggle?: () => void;
  onCancelEdit?: () => void;
}

const LanguageCurrencySection = ({
  isEditing = false,
  onEditToggle,
  onCancelEdit
}: LanguageCurrencySectionProps) => {
  const { userData, refreshUserData } = useAuth();
  const { t, i18n } = useI18n();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<'it' | 'en'>(userData?.preferred_language || 'it');
  const [selectedCurrency] = useState<string>(getDefaultCurrency().code);

  // Update local state when userData changes
  useEffect(() => {
    if (userData?.preferred_language) {
      setSelectedLanguage(userData.preferred_language);
    }
  }, [userData]);

  const getCurrencyDisplayValue = () => {
    const selectedCurrencyData = currencies.find(c => c.code === selectedCurrency);
    if (!selectedCurrencyData) return "";

    return `${selectedCurrencyData.symbol} ${selectedCurrencyData.code} - ${selectedCurrencyData.name}`;
  };

  const getLanguageDisplayValue = () => {
    const lang = userData?.preferred_language || 'it';
    return lang === 'it' ? 'Italiano' : 'English';
  };

  const handleCancel = () => {
    // Reset to original userData value
    if (userData?.preferred_language) {
      setSelectedLanguage(userData.preferred_language);
    }
    onCancelEdit?.();
  };

  const handleSave = async () => {
    if (!userData?.id) return;
    setIsSaving(true);
    try {
      const { error } = await supabase.rpc('update_my_profile', {
        p_first_name: userData.first_name,
        p_last_name: userData.last_name,
        p_phone: userData.phone,
        p_preferred_language: selectedLanguage
      });

      if (error) {
        console.error('Error updating language preference:', error);
        toast({ title: t('profile.updateError'), variant: 'destructive' });
        return;
      }

      // Change the application language immediately
      await i18n.changeLanguage(selectedLanguage);
      localStorage.setItem('language', selectedLanguage);

      await refreshUserData();

      toast({ title: t('profile.updateSuccess'), variant: 'default' });
      onEditToggle?.();
    } catch (err) {
      console.error('Unexpected error updating language preference:', err);
      toast({ title: t('profile.updateError'), variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-normal flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {t("profile.languageCurrency.title")}
          </CardTitle>
          {onEditToggle && onCancelEdit && (
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    className="flex items-center gap-2"
                    disabled={isSaving}
                  >
                    <X className="h-4 w-4" />
                    {t("common.cancel") || "Annulla"}
                  </Button>
                  <Button
                    onClick={handleSave}
                    className="flex items-center gap-2"
                    disabled={isSaving}
                  >
                    <Save className="h-4 w-4" />
                    {isSaving
                      ? t("common.saving") || "Salvataggio..."
                      : t("common.save") || "Salva"}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={onEditToggle}
                  className="flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  {t("common.edit") || "Modifica"}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Language */}
          <div className="space-y-1">
            <Label className="font-medium text-gray-700">
              {t("profile.languageCurrency.language") || "Lingua"}
            </Label>
            {isEditing ? (
              <Select
                value={selectedLanguage}
                onValueChange={(value) =>
                  setSelectedLanguage(value as "it" | "en")
                }
              >
                <SelectTrigger className="h-12">
                  <SelectValue
                    placeholder={
                      t("profile.languageCurrency.selectLanguage") ||
                      "Seleziona lingua..."
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="it">Italiano</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <>
                <div className="min-h-[46px] flex items-center">
                  <div className="flex-1 text-gray-900">
                    {getLanguageDisplayValue()}
                  </div>
                </div>
                <div className="h-px bg-gray-300 w-full"></div>
              </>
            )}
          </div>

          {/* Currency */}
          <EditableInput
            label={t("profile.languageCurrency.currency") || "Valuta"}
            value={getCurrencyDisplayValue()}
            placeholder={
              t("profile.languageCurrency.selectCurrency") ||
              "Seleziona valuta..."
            }
            isEditing={isEditing}
            onChange={() => {}}
            disabled={true}
            labelSuffix={
              isEditing ? (
                <span className="font-normal text-gray-500">
                  ({t("common.notEditable") || "non modificabile"})
                </span>
              ) : undefined
            }
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default LanguageCurrencySection;
